import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/components/customAxios/Axios";
import { useToast } from "@/components/toast";
import { useLoader } from "@/context/loaderContext";
import useBreadCrumb from "@/context/BreadCrumbContext";
import logo from "@/assets/talentickaiwhite.webp";
import logo2 from "@/assets/talentick.ai.webp";
import authPhoto from "@/assets/auth.webp";
import Tooltip from "@/components/Tooltip";

interface CompanyData {
	name: string;
	address: string;
	email: string;
	phoneNumber: string;
	gstNumber: string;
}

const RegisterCompany = () => {
	const navigate = useNavigate();
	const toast = useToast();
	const { setLoading } = useLoader();
	const { setBreadCrumb } = useBreadCrumb();
	const [companyData, setCompanyData] = useState<CompanyData>({
		name: "",
		address: "",
		email: "",
		phoneNumber: "",
		gstNumber: "",
	});
	const [companyExist, setCompanyExist] = useState(false);
	// const plans: Record<string, string> = {
	// 	Silver: "Basic Plan",
	// 	Golden: "Pro Plan",
	// 	Platinum: "Premium Plan",
	// };
	const currentPlan = localStorage.getItem("plan") || "";

	const getCompanyDetails = async () => {
		try {
			setLoading(true);
			const userID = localStorage.getItem("user");
			const response = await api.get(`/get_company_details?id=${userID}`);
			if (response.status === 200) {
				setCompanyExist(true);
				setCompanyData({
					name: response.data.company.name,
					address: response.data.company.address,
					email: response.data.company.email,
					phoneNumber: response.data.company.phone,
					gstNumber: response.data.company.gst_no,
				});
			}
		} catch (error: any) {
			toast.error({
				type: "background",
				duration: 3000,
				status: "Error",
				title: "Error fetching comappany details",
				description:
					error.response?.data?.error || error.response?.data?.message || "An error occurred, please try again later.",
				open: true,
			});
		} finally {
			setLoading(false);
		}
	};

	const handleUserDataChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
	) => {
		const { name, value } = e.target;
		setCompanyData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const registerCompany = async (e: any) => {
		e.preventDefault();
		if (localStorage.getItem("role") !== "guest") {
			navigate("/change-plan-payment");
			return;
		}
		if (companyExist && localStorage.getItem("role") === "guest") {
			navigate("/payment");
			return;
		}
		try {
			setLoading(true);
			const response = await api.post("/store_company_details", {
				subscription_plan_id: localStorage.getItem("plan"),
				user_id: localStorage.getItem("user"),
				name: companyData.name,
				address: companyData.address,
				email: companyData.email,
				phone: companyData.phoneNumber,
				gst_no: companyData.gstNumber,
			});

			if (response.status === 201) {
				navigate("/payment");
				localStorage.setItem("companyId", response.data.company_id);
				toast.success({
					type: "background",
					duration: 3000,
					status: "Success",
					title: "Company Registered",
					description: "Your company has been registered successfully.",
					open: true,
				});
			}
		} catch (error: any) {
			setLoading(false);
			toast.error({
				type: "background",
				duration: 3000,
				status: "Error",
				title: "Error Registering Company",
				description: error.response?.data?.error || error.response?.data?.message || "An unknown error occurred",
				open: true,
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		setBreadCrumb({
			mainLabel: {
				label: "Select Plan",
				link: "/select-plan",
			},
			currentLabel: "Billing Details",
		});
		getCompanyDetails();
	}, []);
	return (
		<>
			<Header />
			<div className="mx-auto flex flex-col md:flex-row items-center gap-8 bg-white">
				{/* Mobile Logo */}
				<div className="w-full block md:hidden mb-6">
					<Link to="/" className="flex gap-2 items-center fixed top-10">
						<svg width="20" height="18" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M10.0154 0.733154L4.23065 6.51518C3.93728 6.79466 3.93728 7.23137 4.23065 7.51085L10.0155 13.2928"
								stroke="#7F7F7F"
								strokeMiterlimit="2.613"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
						<img src={logo2} className="h-5" alt="Logo" />
					</Link>
				</div>

				{/* Left Side - Image and Text */}
				<div className="relative w-[55%] h-[calc(100vh-70px)] hidden md:block">
					<img
						src={authPhoto}
						alt="Background"
						className="w-full h-[calc(100vh-70px)] object-cover shadow-md"
					/>
					<div className="absolute inset-0 opacity-40 h-[calc(100vh-70px)] bg-[#32413D]"></div>

					{/* Logo */}
					<Link to="/">
						<img
							src={logo}
							className="absolute top-8 left-8 max-h-10 cursor-pointer z-50"
							alt="Logo"
						/>
					</Link>

					{/* Centered Text Box */}
					<div className="absolute inset-0 flex flex-col items-center justify-center px-8">
						<div className="bg-white/30 backdrop-blur-[70px] rounded-lg p-8 md:p-10 w-full max-w-[650px] text-white">
							<h2 className="text-2xl lg:text-5xl font-bold mb-4">
								Register Your Company
							</h2>
							<p className="text-sm md:text-lg font-light">
								Join talentick.ai to streamline your hiring process. Register your company to access AI-powered recruitment tools, automated assessments, and smart candidate matching.
							</p>
						</div>
					</div>

					{/* Bottom text */}
					<div className="absolute bottom-8 w-full text-center text-white text-sm lg:text-base font-light">
						Transform your hiring process with AI
					</div>
				</div>

				{/* Right Side - Company Registration Form */}
				<div className="w-full md:w-[40%] md:h-[calc(100vh-70px)] overflow-y-auto md:px-0 px-4">
					<div className="flex flex-col items-center justify-center py-4 min-h-full">
						<div className="w-full">
							<div className="flex items-center gap-3 mb-6">
								<h2 className="text-2xl font-semibold">Billing Details</h2>
								{companyExist && (
									<Tooltip>
										<p>You can update the details in the settings page</p>
									</Tooltip>
								)}
							</div>

							<form className="space-y-4" onSubmit={registerCompany}>
								<div>
									<label className="block text-sm font-medium mb-1">
										Organization Name
									</label>
									<input
										readOnly={companyExist}
										onChange={(e) => handleUserDataChange(e)}
										value={companyData.name}
										name="name"
										type="text"
										required
										className={`${companyExist ? "cursor-not-allowed" : ""
											} w-full border rounded-md px-3 py-2 `}
									/>
								</div>
								<div>
									<label className="block text-sm font-medium mb-1">
										Organization Email
									</label>
									<input
										readOnly={companyExist}
										value={companyData.email}
										onChange={(e) => handleUserDataChange(e)}
										name="email"
										type="email"
										required
										className={`${companyExist ? "cursor-not-allowed" : ""
											} w-full border rounded-md px-3 py-2 `}
									/>
								</div>
								<div>
									<label className="block text-sm font-medium mb-1">
										Phone Number
									</label>
									<input
										readOnly={companyExist}
										value={companyData.phoneNumber}
										type="tel"
										onChange={(e) => handleUserDataChange(e)}
										name="phoneNumber"
										required
										className={`${companyExist ? "cursor-not-allowed" : ""
											} w-full border rounded-md px-3 py-2 `}
									/>
								</div>
								<div>
									<label className="block text-sm font-medium mb-1">
										Organization Address
									</label>
									<textarea
										readOnly={companyExist}
										value={companyData.address}
										onChange={(e) => handleUserDataChange(e)}
										name="address"
										rows={3}
										autoComplete="address"
										required
										placeholder="Address"
										className={`${companyExist ? "cursor-not-allowed" : ""
											} w-full border rounded-lg py-2 px-3  dark:text-zinc-300 bg-transparent `}
									/>
								</div>
								<div>
									<label className="block text-sm font-medium mb-1">
										GST No. (optional)
									</label>
									<input
										value={companyData.gstNumber}
										readOnly={companyExist}
										onChange={(e) => handleUserDataChange(e)}
										name="gstNumber"
										type="text"
										className={`${companyExist ? "cursor-not-allowed" : ""
											} w-full border rounded-md px-3 py-2 `}
									/>
								</div>
								<div>
									<label className="block text-sm font-medium mb-1">
										Selected Plan
									</label>
									<input
										readOnly
										value={currentPlan}
										type="text"
										className="w-full border rounded-md px-3 py-2 cursor-not-allowed"
									/>
								</div>

								{/* Terms Checkbox */}
								<div className="flex items-center space-x-2 text-sm">
									<input type="checkbox" className="accent-blue-600" required onInvalid={(e) =>
										(e.target as HTMLInputElement).setCustomValidity("Please accept the terms to proceed to payment.")
									}
										onInput={(e) =>
											(e.target as HTMLInputElement).setCustomValidity("")
										} />
									<label>
										I agree to the{" "}
										<Link to="/terms-of-service" target="__blank" className="text-blue-600 underline">Terms and Conditions</Link>
									</label>
								</div>

								{/* Submit Button */}
								<button
									type="submit"
									className="w-full bg-gray-800 text-white py-2 rounded-md hover:bg-gray-700 transition"
								>
									Proceed to Payment
								</button>
							</form>

						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default RegisterCompany;
